import { useCallback, useEffect, useState } from 'react';
import { Loader, Modal } from '../../../components';
import TransactionLabelField from '../../../components/transaction/TransactionLabelField';
import { InsuredCustomer } from '../../../types/interfaces/reports.interface';
import { getInsuredCustomerById } from '../../../api/insuredCustomersApi';
import { showToast } from '../../../services/toast.service';
import { centsToDollars } from '../../../services/account.service';
import { toastMessages } from '../../../constants/errorMessages';
import { ADDRESS_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../../constants/common';

export type InsuredCustomerDetailsModalProps = {
  isOpen: boolean;
  width?: number;
  onClose: () => void;
  shippingTransactionId: number | undefined;
};

const InsuredCustomerDetailsModal = ({
  isOpen,
  width = 600,
  onClose,
  shippingTransactionId,
}: InsuredCustomerDetailsModalProps) => {
  const [insuredCustomer, setInsuredCustomer] = useState<InsuredCustomer>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handlePageLoad = useCallback(async () => {
    if (shippingTransactionId) {
      try {
        setIsLoading(true);
        const { data: insuredCustomerResponse } = await getInsuredCustomerById(shippingTransactionId);
        setInsuredCustomer(insuredCustomerResponse);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  }, [shippingTransactionId]);

  useEffect(() => {
    handlePageLoad();
  }, [handlePageLoad]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} width={width} ignoreBackdrop>
        <div className="insured-customer-details-modal">
          {isLoading ? (
            <Loader loaderSize="small" pageLoader />
          ) : (
            <div className="insured-customer-details">
              <div className="inventory-action card">
                <h2>{LABEL_CONSTANTS.CUSTOMER_INSURANCE_DETAILS}</h2>
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.ACCOUNT_NAME}:`}
                  value={<label>{insuredCustomer?.account?.name || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${ADDRESS_CONSTANTS.ADDRESS}:`}
                  value={<label>{insuredCustomer?.shippingAddress || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.EMAIL}:`}
                  value={<label>{insuredCustomer?.account?.contactEmail || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.PHONE_NO}:`}
                  value={<label>{insuredCustomer?.account?.contactPhone}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.DATE_SUBMITTED}:`}
                  value={
                    <label>{`${
                      new Date(insuredCustomer?.billedDate?.toString() as string).toLocaleDateString('en-US') || '--'
                    }`}</label>
                  }
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.INSURANCE_AMOUNT}: `}
                  value={<label>{`$${centsToDollars(insuredCustomer?.insuredAmount) || '--'}`}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.INSURANCE_PREMIUM}:`}
                  value={<label>{`$${centsToDollars(insuredCustomer?.insurancePremium) || '--'}`}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.PROCESSING_FEE}:`}
                  value={<label>{`$${centsToDollars(insuredCustomer?.processingFee) || '--'}`}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.TRACKING_NUMBER}:`}
                  value={<label>{insuredCustomer?.trackingNumber || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.SHIPMENT_ID}:`}
                  value={<label>{insuredCustomer?.shipmentId || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.SERVICE_CODE}:`}
                  value={<label>{insuredCustomer?.serviceCode || '--'}</label>}
                />
                <TransactionLabelField
                  label={`${LABEL_CONSTANTS.CARRIER_CODE}:`}
                  value={<label>{insuredCustomer?.carrierCode || '--'}</label>}
                />
              </div>
              <div className="flex-right">
                <button className="button medium green close-button" onClick={() => onClose()}>
                  {BUTTON_CONSTANTS.CLOSE}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default InsuredCustomerDetailsModal;
